import { motion } from "framer-motion";
import { Globe, Phone, ShieldCheck } from "lucide-react";
import ShopButton from "@/components/mall/ShopButton";
import { BillboardFrame, Bullets, RoomNumber } from "@/components/mall/BillboardKit";

const BLUE = "#2563eb";
const CYAN = "#22d3ee";

const CARDS = [
    "Your site goes live in 7 days, not 6 months.",
    "It costs a few thousand dollars, not $50,000.",
    "It is a real website with a real backend, not a template.",
    "It is built to sell, not to sit there.",
];

const DEAL = [
    "We build the site for you.",
    "We keep it running.",
    "We handle the security and the updates.",
    "You pay the monthly fee and we take care of the rest.",
];

/** A rack of servers with blinking LEDs and fiber lines running out of it. */
function ServerRack() {
    const units = Array.from({ length: 7 });
    return (
        <svg viewBox="0 0 300 380" className="h-full w-full" data-testid="website-rack" aria-hidden="true">
            <defs>
                <linearGradient id="rack-steel" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0" stopColor="#0f172a" /><stop offset="0.5" stopColor="#1e293b" /><stop offset="1" stopColor="#0f172a" />
                </linearGradient>
                <linearGradient id="fiber-glow" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0" stopColor={CYAN} stopOpacity="0" /><stop offset="0.5" stopColor={CYAN} /><stop offset="1" stopColor={BLUE} stopOpacity="0" />
                </linearGradient>
            </defs>
            {/* fiber lines behind the rack */}
            {[40, 90, 140, 190, 240, 290, 340].map((y, i) => (
                <path key={y} className={i % 2 ? "fiber" : "fiber-slow"} fill="none" strokeWidth="2" stroke="url(#fiber-glow)"
                    d={`M -20 ${y} C 60 ${y - 30}, 120 ${y + 40}, 320 ${y - 10}`} style={{ animationDelay: `${i * 0.4}s` }} />
            ))}
            <rect x="70" y="10" width="160" height="360" rx="6" fill="url(#rack-steel)" stroke="#334155" strokeWidth="2" />
            {units.map((_, i) => {
                const y = 24 + i * 48;
                return (
                    <g key={i}>
                        <rect x="82" y={y} width="136" height="38" rx="3" fill="#020617" stroke="#1e293b" />
                        <rect x="90" y={y + 8} width="60" height="22" rx="2" fill="#0f172a" stroke="#1e293b" />
                        {[0, 1, 2, 3, 4].map((k) => (
                            <rect key={k} x={94 + k * 11} y={y + 12} width="7" height="14" fill="#111827" stroke="#1f2937" strokeWidth="0.6" />
                        ))}
                        {[0, 1, 2].map((k) => (
                            <circle key={k} className="led" cx={168 + k * 14} cy={y + 19} r="3.2"
                                fill={k === 0 ? "#22c55e" : k === 1 ? CYAN : "#f59e0b"}
                                style={{ animationDelay: `${((i * 3 + k) % 7) * 0.23}s` }} />
                        ))}
                        <rect x="204" y={y + 9} width="8" height="20" rx="1" fill="#1e293b" />
                    </g>
                );
            })}
            {/* fiber jumpers running down the right side */}
            {[0, 1, 2].map((k) => (
                <path key={k} className="fiber" fill="none" strokeWidth="2.2" stroke={k === 1 ? BLUE : CYAN}
                    d={`M ${236 + k * 8} 30 C ${260 + k * 10} 120, ${250 + k * 6} 260, ${236 + k * 8} 350`} style={{ animationDelay: `${k * 0.7}s` }} />
            ))}
        </svg>
    );
}

/** Room 09 — the websites billboard: a server room lit by LEDs and fiber. */
export default function Website() {
    return (
        <BillboardFrame testid="frame-website" room="website" kind="wide" note="every shop that builds sites">
        <section className="dark-block relative overflow-hidden" data-testid="mall-room-website"
            style={{ background: "linear-gradient(120deg, #020617 0%, #061a2b 45%, #020617 100%)" }}>
            {/* fiber lines running across the whole billboard */}
            <svg className="pointer-events-none absolute inset-0 h-full w-full opacity-60" viewBox="0 0 1600 500" preserveAspectRatio="none" aria-hidden="true">
                {[80, 160, 260, 340, 430].map((y, i) => (
                    <path key={y} className={i % 2 ? "fiber-slow" : "fiber"} fill="none" strokeWidth="1.6"
                        stroke={i % 2 ? BLUE : CYAN} style={{ animationDelay: `${i * 0.6}s` }}
                        d={`M -10 ${y} C 300 ${y - 80}, 700 ${y + 90}, 1000 ${y - 40} S 1400 ${y + 60}, 1620 ${y}`} />
                ))}
            </svg>
            <div className="pointer-events-none absolute -left-20 top-0 h-72 w-72 rounded-full blur-[130px]" style={{ background: "rgba(34,211,238,0.22)" }} />
            <div className="pointer-events-none absolute bottom-0 right-1/3 h-64 w-64 rounded-full blur-[120px]" style={{ background: "rgba(37,99,235,0.3)" }} />

            <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 py-10 sm:px-6 md:py-12 lg:grid-cols-12">
                <div className="lg:col-span-7">
                    <RoomNumber n="09" room="Websites" accent={CYAN} dark className="mt-6" />
                    <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                        className="mt-4 font-display text-4xl font-black leading-[0.96] tracking-tighter text-white sm:text-5xl" data-testid="website-headline">
                        WE BUILD CUSTOM WEBSITES<br /><span style={{ color: CYAN }}>FOR ANY BUSINESS.</span>
                    </motion.h2>
                    <p className="mt-3 font-display text-base text-zinc-400 md:text-lg">
                        The website you are on right now is one of them.
                    </p>
                    <p className="mt-4 max-w-xl text-base text-zinc-300 md:text-lg" data-testid="website-body">
                        If your business is not on the internet, your business will be out of business. We build websites
                        for print shops, restaurants, contractors, stores and any other business. We build the site, we keep
                        it running and we handle the security. You pay the monthly fee and we take care of the rest.
                    </p>
                    <Bullets items={DEAL} accent={CYAN} dark className="mt-7" testid="website-deal" />
                    <div className="mt-8 flex flex-wrap gap-3">
                        <ShopButton room="website" tone="blue" testid="room-website-cta"
                            title="Who do you want to build your website?" path="/website-design">
                            <Globe size={17} /> Build my website
                        </ShopButton>
                        <a href="tel:+13475733875" data-testid="room-website-call"
                            className="inline-flex items-center gap-2 rounded-full border px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-zinc-100 transition-colors hover:border-white/60"
                            style={{ borderColor: "rgba(255,255,255,0.25)" }}>
                            <Phone size={16} /> Call and talk it through
                        </a>
                    </div>
                    <p className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-400">
                        <ShieldCheck size={12} style={{ color: CYAN }} /> Hosting, updates and security are included.
                    </p>
                </div>

                <div className="lg:col-span-5">
                    <div className="grid items-center gap-5 sm:grid-cols-[1fr_1.1fr]">
                        <div className="h-72 sm:h-80"><ServerRack /></div>
                        <div className="grid gap-3">
                            {CARDS.map((t, i) => (
                                <motion.div key={t} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }} transition={{ delay: i * 0.07 }}
                                    className="rounded-2xl border p-4 text-sm text-zinc-200"
                                    style={{ borderColor: "rgba(34,211,238,0.25)", background: "rgba(2,6,23,0.6)" }}>
                                    {t}
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
        </BillboardFrame>
    );
}
